// 给按钮注入特定的链接
import { IOrder, IOrderAction, IOrderActionWithStyle, ISubOrder } from 'types/order';
import * as ActionConst from 'services/v2/order/const';
import {
    isF7Order,
    isKsdOrder,
    isMainOrder,
    isVoOrder,
    isInOrderCenter,
    isVoucherOrder
} from 'src/services/v2/order/utils';
import { hasMultiCommentOrder } from 'services/v2/order/comment';
import bridge from 'src/libs/bridge';
import env from 'src/libs/env';
import { isPreRefundOrder } from './desc-status';
export interface IGetOutOrderParams {
    platform: string;
    productId: string;
    orderId: string;
    outProductId: string;
    comeFrom?: string;
}

const getAftersaleLink = (order: IOrder | ISubOrder) => {
    let link = '';
    let title = '';
    if (order.shop_order_status === 101) {
        title = '选择售后商品';
        link = `/views/newaftersale/list?id=${order.order_id}`;
        return [link, title];
    }
    const hasSubOrders = order.sub_orders && order.sub_orders.length > 1;
    if (hasSubOrders) {
        link = `/views/newaftersale/select?id=${order.order_id}`;
        title = '选择售后商品';
    } else {
        link = `/views/newaftersale/midpage?id=${order.order_id}`;
        title = '申请售后';
    }
    return [link, title];
};

const getPayLink = (order: IOrder | ISubOrder) => {
    let link = '';
    let type = 'jump';
    const { order_id: orderId } = order;

    if (isKsdOrder(order)) {
        link = `https://ksd.snssdk.com/pay.html?order_id=${orderId}&combo_id=
                            ${order.combo_id}&product_id=${order.product_id}`;
    } else if (isF7Order(order)) {
        link = `https://ta.snssdk.com/f7/pay.html?order_id=${orderId}&combo_id=
                            ${order.combo_id}`;
    } else if (isVoOrder(order)) {
        link = `https://ta.snssdk.com/vo/pay.html?order_id=${orderId}&combo_id=
                            ${order.combo_id}`;
    } else {
        link = `/views/neworder/index?id=${orderId}`;
        type = 'showPayForms';
    }
    return [link, type];
};

const getRefundLink = (order: IOrder | ISubOrder) => {
    let link = '';
    if (order.final_status === 2) {
        link = `/views/newaftersale/return?id=${order.order_id}&before_sale=1&only_refund=1`;
    } else if (
        // poi 订单退款失败且仍然可以申请售后则进入售后仅退款
        isVoucherOrder(order) &&
        (order.final_status === 3 || (order.final_status === 33 && order.can_apply_aftersale))
    ) {
        link = `/views/newaftersale/return?id=${order.order_id}&before_sale=0&only_refund=1`;
    }
    return [link, '申请退款'];
};

const getLogisticsLink = (order: IOrder | ISubOrder) => {
    let title = '物流详情';
    let link = `/views/neworder/express?id=${order.order_id}`;
    if (order.is_multi_package) {
        link = `/views/neworder/expresslist?id=${order.order_id}`;
        title = '物流列表';
    }
    return [link, title];
};

const getLink = (
    order: IOrder | ISubOrder,
    orderAction: IOrderActionWithStyle
): {
    link: string;
    title: string;
    type: string;
} => {
    const { final_status: finalStatus } = order;
    let link = '';
    let title = '';
    let type = 'jump';
    switch (orderAction.name) {
        case ActionConst.APPLY_AFTERSALE:
            [link, title] = getAftersaleLink(order);
            break;
        case ActionConst.CHECKOUT_LOGISTICS:
            // 查看物流按钮链接取决于订单是否多包裹发货
            [link, title] = getLogisticsLink(order);
            break;
        case ActionConst.PAY:
            [link, type] = getPayLink(order);
            break;
        case ActionConst.RETURN_GOODS_INFO:
            link = `/views/newaftersale/returnForm?id=${order.order_id}`;
            break;
        case ActionConst.CHECKOUT_REFUND:
            link = `/views/newaftersale/detail?id=${order.order_id}&before_sale=${
                isPreRefundOrder(order.shop_order_status) ? 1 : 0
            }&isExchange=${Number(order.aftersale_type === 3)}`;
            title = '售后详情';
            break;
        case ActionConst.CHECKOUT_RETURN_GOODS:
            link = `/views/newaftersale/index?id=${order.order_id}&refund=${
                [16, 17, 18, 19, 20, 21, 25, 26].indexOf(finalStatus) !== -1 && isMainOrder(order)
                    ? 1
                    : ''
            }`;
            break;
        case ActionConst.CHECKOUT_COMMENT:
            link = `/views/newcomment/snap?order_id=${order.order_id}`;
            break;
        case ActionConst.COMMENT_WITH_COUPON:
        case ActionConst.PUBLISH_COMMENT:
            link = `/views/newcomment/publish?order_id=${order.order_id}`;
            break;
        case ActionConst.APPEND_COMMENT:
            link = hasMultiCommentOrder(order)
                ? `/views/neworder/comment?order_id=${order.order_id}`
                : `/views/newcomment/snap?is_append=1&order_id=${order.order_id}`;
            break;
        case ActionConst.EDIT_ADDRESS:
            link = `/views/neworder/editAddress?id=${order.order_id}`;
            break;
        case ActionConst.CHECKOUT_CERTIFICATE:
            link = `/views/neworder/certificate?order_id=${order.order_id}`;
            break;
        case ActionConst.CHECK_VOUCHER:
            link = isInOrderCenter() ? `/views/neworder/index?id=${order.order_id}` : '';
            break;
        case ActionConst.APPLY_REFUND:
            [link, title] = getRefundLink(order);
            break;
        default:
            break;
    }

    return {
        link,
        title,
        type
    };
};

export const getOrderActionLink = (
    orderAction: IOrderActionWithStyle[],
    order: IOrder | ISubOrder
): IOrderAction[] => {
    return orderAction.reduce((actionWithLink: IOrderAction[], _orderAction) => {
        actionWithLink.push({
            ..._orderAction,
            link: getLink(order, _orderAction)
        });

        return actionWithLink;
    }, []);
};

// 后续接入教育线产品只需要 1. platform填入下方 2. getOutOrderLink的urlMap 增加对应的跳转链接即可。
export const isByteEduProduct = (platform: string = '') =>
    ['learning', 'et', 'ep', 'ez'].indexOf(platform) !== -1;

export const getOutOrderLink = async ({
    platform,
    productId,
    orderId,
    outProductId,
    comeFrom
}: IGetOutOrderParams) => {
    const urlMap: { [key: string]: string } = {
        et: `https://tangyuanyingyu.com/share/result?jrtm_product_id=${productId}&jrtm_order_id=${orderId}`,
        learning: `https://learning.snssdk.com/static-cdn/wallet_bundles/learning_douyin_wap/mp-guide.html?product_id=${productId}&out_product_id=${outProductId}`,
        ez: `https://m.openlanguage.com/m/g/live_broadcast?order_id=${orderId}`,
        ep: `https://student-api.iyincaishijiao.com/ep/h5/courseList/?out_product_id=${outProductId}&order_id=${orderId}&enter_from=${comeFrom}&disableBounces=1&hide_nav_bar=1&hide_status_bar=1&should_full_screen=1&loading_bgcolor=fff&adinfo=0&auto_play_bgm=1&allowMediaAutoPlay=1`
    };
    let url = urlMap[platform];

    if (!isByteEduProduct(platform) || !url) return '';

    const extra = await new Promise((resolve) => {
        bridge
            .call('appInfo')
            .then((info: any) => {
                // 没appVersion和appName则直接返回
                if (!info || !info.appVersion || !info.appName) resolve('');
                const { appName, appVersion } = info;

                const outUrl = urlMap[platform || ''];

                if (!outUrl) resolve('');

                const versionCode = appVersion.toString().replace(/\./g, '');
                resolve(
                    `&os=${env.isIOS ? 'ios' : 'android'}&app=${appName}&version=${versionCode}`
                );
            })
            .catch(() => {
                resolve('');
            });
    });

    return url + extra;
};
